import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, MessageCircle, ShieldCheck, Smartphone, Link2, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { getPortalPbToken, getPortalPocketBaseUrl } from '../../../constants';
import { portalKanzleiChatSurfaceClass } from '../portalAppearance';
import { usePortalAppearance } from '../portalAppearanceContext';
import {
  ELEMENT_DOWNLOAD_URL,
  isAllowedElementEmbedSrc,
  portalMatrixHomeserverUrl,
} from '../portalMatrixOrigins';
import { GLOCKE_SPRUNG_EVENT, peekGlockeSprung, type GlockeSprung } from '../portalGlockeSprung';
import {
  KANZLEI_CHAT_HISTORY_UNLOADED,
  KANZLEI_CHAT_NOT_A_MEMBER,
  KANZLEI_CHAT_NO_ELEMENT_ACCOUNT,
  KANZLEI_CHAT_SEAT_MXID_ONLY,
  kanzleiChatCreateRequest,
  kanzleiChatCreateResult,
  kanzleiChatInviteExistingRequest,
  kanzleiChatInviteExistingResult,
  KANZLEI_CHAT_ROOM_NO_STORED_SEATS,
  kanzleiChatArchiveEmptyResult,
  kanzleiChatEmptyRoomDropped,
  kanzleiChatRemoveOwnSeatRequest,
  kanzleiChatRemoveRequest,
  kanzleiChatRemoveResult,
  kanzleiChatRoomLabel,
  kanzleiChatRooms,
  kanzleiChatSeatInviteLines,
  kanzleiChatSeatLabel,
  omitSeatLeftKanzleiRoom,
  readElementRoomsResponse,
  readKanzleiChatSeat,
  elementSeatAttachDue,
  elementSeatAttachMatchesSeat,
  elementSeatAttachResult,
  kanzleiChatSeatAttachRequest,
  markElementSeatAttach,
  rememberSeatLoginToken,
  resolveKanzleiChatPane,
  seatIdInRoomSeatIds,
  takeSeatLoginTokenForIframe,
  seatsForKanzleiInvite,
  seatsInSelectedKanzleiRoom,
  type ElementRoomOption,
  type KanzleiChatSeat,
} from '../elementSeatInvite';
import { noteSitzGlockePortalAction, sitzGlockeActorFromSeat } from '../sitzGlockeStore';

export {
  ELEMENT_DOWNLOAD_URL,
  ELEMENT_WEB_ORIGIN,
  FIRMA_ELEMENT_WEB_ORIGIN,
  FIRMA_MATRIX_HOMESERVER_URL,
  LABOR_ELEMENT_WEB_ORIGIN,
  LABOR_MATRIX_HOMESERVER_URL,
  MATRIX_HOMESERVER_URL,
  MATRIX_SERVER_NAME,
  buildElementLoginUrl,
  buildElementRoomUrl,
  isAllowedElementEmbedSrc,
  portalElementWebOrigin,
  portalMatrixHomeserverUrl,
} from '../portalMatrixOrigins';

/** Nur 0/1 — keine Namen, keine Mail. Default aufgeklappt. */
const KANZLEI_CHAT_ROOMS_OPEN_KEY = 'arkorbit.portal.kanzleiChat.roomsOpen';

function readKanzleiChatRoomsOpen(): boolean {
  try {
    if (typeof sessionStorage === 'undefined') return true;
    return sessionStorage.getItem(KANZLEI_CHAT_ROOMS_OPEN_KEY) !== '0';
  } catch {
    return true;
  }
}

function writeKanzleiChatRoomsOpen(open: boolean): void {
  try {
    sessionStorage.setItem(KANZLEI_CHAT_ROOMS_OPEN_KEY, open ? '1' : '0');
  } catch {
    /* Sitzung ohne Storage — Schalter gilt nur für diese Ansicht. */
  }
}

/** Nur eine Pixelzahl. Einklappen schreibt hier nicht. */
const KANZLEI_CHAT_ROOMS_WIDTH_KEY = 'arkorbit.portal.kanzleiChat.roomsWidth';
const KANZLEI_CHAT_ROOMS_WIDTH_MIN = 240;
const KANZLEI_CHAT_ROOMS_WIDTH_DEFAULT = 288;

function kanzleiChatRoomsWidthMax(): number {
  if (typeof window === 'undefined') return 480;
  return Math.max(KANZLEI_CHAT_ROOMS_WIDTH_MIN, Math.round(window.innerWidth * 0.25));
}

function clampKanzleiChatRoomsWidth(px: number): number {
  const max = kanzleiChatRoomsWidthMax();
  if (!Number.isFinite(px)) return Math.min(KANZLEI_CHAT_ROOMS_WIDTH_DEFAULT, max);
  return Math.min(max, Math.max(KANZLEI_CHAT_ROOMS_WIDTH_MIN, Math.round(px)));
}

function readKanzleiChatRoomsWidth(): number {
  try {
    if (typeof sessionStorage === 'undefined') return clampKanzleiChatRoomsWidth(KANZLEI_CHAT_ROOMS_WIDTH_DEFAULT);
    const raw = sessionStorage.getItem(KANZLEI_CHAT_ROOMS_WIDTH_KEY);
    if (!raw || !/^\d+$/.test(raw)) return clampKanzleiChatRoomsWidth(KANZLEI_CHAT_ROOMS_WIDTH_DEFAULT);
    return clampKanzleiChatRoomsWidth(Number(raw));
  } catch {
    return clampKanzleiChatRoomsWidth(KANZLEI_CHAT_ROOMS_WIDTH_DEFAULT);
  }
}

function writeKanzleiChatRoomsWidth(px: number): void {
  try {
    sessionStorage.setItem(KANZLEI_CHAT_ROOMS_WIDTH_KEY, String(clampKanzleiChatRoomsWidth(px)));
  } catch {
    /* Sitzung ohne Storage — Breite gilt nur für diese Ansicht. */
  }
}

/** Schwarzer Grund, feine lila Kante — wie die Aktenliste. */
const KANZLEI_CHAT_GROUND = 'bg-[#07070a]';
const KANZLEI_CHAT_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
/** Knopf wie Neue Akte und Öffnen. */
const KANZLEI_CHAT_BTN =
  'inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45';
/** Icon-Knopf wie die Leiste im Hauptreiter. */
const KANZLEI_CHAT_ICON =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-45';
const KANZLEI_CHAT_FIELD =
  'w-full rounded-md border border-violet-400/30 bg-[#07070a] text-sm text-slate-100 placeholder:text-slate-600 focus:border-violet-400 focus:outline-none';

/**
 * Dieselbe Würfel-Drehung wie Arko ON/off: rotateX, 500ms, Perspektive 280.
 * Nur wo die Raumspalte auf- und zuklappt.
 */
function KanzleiChatCube({
  open,
  front,
  back,
}: {
  open: boolean;
  front: React.ReactNode;
  back: React.ReactNode;
}) {
  const face =
    'absolute inset-0 inline-flex h-full w-full items-center justify-center rounded-md [backface-visibility:hidden]';
  return (
    <span className="relative block h-7 w-7 shrink-0 [perspective:280px]" aria-hidden>
      <span
        className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d]"
        style={{ transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
      >
        <span className={`${face} border border-violet-400/40 bg-violet-600/30 text-violet-200`}>
          {front}
        </span>
        <span className={`${face} bg-violet-600 text-white`} style={{ transform: 'rotateX(180deg)' }}>
          {back}
        </span>
      </span>
    </span>
  );
}

/** Element-Web inline (kein window.open als Default). Fallback-Tab nur sekundär. */
export const ElementRoomEmbed: React.FC<{
  src: string;
  testIdPrefix: string;
  grow?: boolean;
  showFallback?: boolean;
  /** Nur der Kanzlei-Chat setzt die lila Kante. Default bleibt für Akte und Diktat. */
  frameClassName?: string;
  onLoad?: () => void;
  onError?: () => void;
}> = ({
  src,
  testIdPrefix,
  grow = false,
  showFallback = true,
  frameClassName,
  onLoad,
  onError,
}) => {
  if (!isAllowedElementEmbedSrc(src)) {
    return (
      <p className="text-amber-200/90 text-sm leading-relaxed" data-testid={`${testIdPrefix}-embed-blocked`}>
        Element-Chat für diese Umgebung nicht eingebettet.
      </p>
    );
  }
  return (
    <div
      className={grow ? 'flex min-h-0 h-full flex-1 flex-col gap-1.5' : 'space-y-2'}
      data-testid={`${testIdPrefix}-embed-wrap`}
    >
      <iframe
        key={src}
        src={src}
        title="Kanzlei-Raum (Element)"
        data-testid={`${testIdPrefix}-embed`}
        className={
          frameClassName
            ? `${grow ? 'w-full h-full min-h-[12rem] flex-1 rounded-lg ' : 'w-full min-h-[32rem] h-[70vh] max-h-[40rem] rounded-lg '}${frameClassName}`
            : grow
              ? 'w-full h-full min-h-[12rem] flex-1 rounded-lg border border-white/10 bg-[#0b0d10]'
              : 'w-full min-h-[32rem] h-[70vh] max-h-[40rem] rounded-lg border border-white/10 bg-[#0b0d10]'
        }
        referrerPolicy="strict-origin-when-cross-origin"
        allow="microphone; clipboard-write"
        onLoad={onLoad}
        onError={onError}
      />
      {showFallback ? (
      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        data-testid={`${testIdPrefix}-fallback-tab`}
        className="inline-flex shrink-0 items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300"
      >
        <ExternalLink className="w-3 h-3" aria-hidden />
        Falls der Chat hier leer bleibt: in neuem Tab öffnen
      </a>
      ) : null}
    </div>
  );
};

export interface MatrixViewProps {
  matrixRoomId: string;
  /** MXID dieses Portal-Sitzes. Leer heißt: kein Element-Konto. */
  seatMxid?: string;
  /** Sitz-Id, nur um ein Attach-Token im Speicher zu finden. */
  seatId?: string;
  kanzleiId?: string;
  /** IT-Admin / Primär: Raum anlegen und Sitze einladen. */
  canManageRooms?: boolean;
  onOpenDiktatAnleitung: () => void;
}

export const MatrixView: React.FC<MatrixViewProps> = ({
  seatMxid = '',
  seatId = '',
  kanzleiId = '',
  canManageRooms = false,
  onOpenDiktatAnleitung,
}) => {
  const appearance = usePortalAppearance();
  const kanzleiChatLight = portalKanzleiChatSurfaceClass(appearance);
  const embedRef = useRef<HTMLDivElement>(null);
  const [seats, setSeats] = useState<KanzleiChatSeat[]>([]);
  const [apiRooms, setApiRooms] = useState<ElementRoomOption[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [roomName, setRoomName] = useState('');
  const [inviteIds, setInviteIds] = useState<string[]>([]);
  const [reloadIds, setReloadIds] = useState<string[]>([]);
  const [createNote, setCreateNote] = useState('');
  const [reloadNote, setReloadNote] = useState('');
  const [reloading, setReloading] = useState(false);
  const [removeNote, setRemoveNote] = useState('');
  const [removingSeatId, setRemovingSeatId] = useState('');
  const [removingRoom, setRemovingRoom] = useState(false);
  const [leftSeatIds, setLeftSeatIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [catalogTick, setCatalogTick] = useState(0);
  const [attachTick, setAttachTick] = useState(0);
  const [historyFailed, setHistoryFailed] = useState(false);
  const [loginAttempt, setLoginAttempt] = useState<{
    seatId: string;
    seatMxid: string;
    roomId: string;
    token: string;
  } | null>(null);
  const [roomsOpen, setRoomsOpen] = useState(readKanzleiChatRoomsOpen);
  const [roomsWidth, setRoomsWidth] = useState(readKanzleiChatRoomsWidth);
  const roomsWidthRef = useRef(roomsWidth);
  const roomsDragRef = useRef<{ startX: number; startW: number } | null>(null);
  const createdRoomsRef = useRef<ElementRoomOption[]>([]);
  const pendingOpenIdRef = useRef('');

  const rooms = useMemo(
    () => kanzleiChatRooms({ apiRooms, seats: [], selfRoomId: '' }),
    [apiRooms],
  );
  const glockeRoomRef = useRef('');
  useEffect(() => {
    const openRoom = (refId: string) => {
      if (!refId || glockeRoomRef.current === refId) return;
      const room = rooms.find((row) => row.id === refId);
      if (!room) {
        pendingOpenIdRef.current = refId;
        return;
      }
      glockeRoomRef.current = refId;
      pendingOpenIdRef.current = '';
      setSelectedId(room.id);
      setRoomsOpen(true);
    };
    const pending = peekGlockeSprung();
    if (pending?.source === 'element') openRoom(pending.refId);
    const onSprung = (event: Event) => {
      const detail = (event as CustomEvent<GlockeSprung>).detail;
      if (!detail || detail.source !== 'element') return;
      glockeRoomRef.current = '';
      openRoom(detail.refId);
    };
    window.addEventListener(GLOCKE_SPRUNG_EVENT, onSprung);
    return () => window.removeEventListener(GLOCKE_SPRUNG_EVENT, onSprung);
  }, [rooms]);
  const inviteSeats = useMemo(() => seatsForKanzleiInvite(seats), [seats]);
  const seatsInRoom = useMemo(() => {
    const room = rooms.find((row) => row.id === selectedId) || null;
    return omitSeatLeftKanzleiRoom(seatsInSelectedKanzleiRoom(seats, room), leftSeatIds);
  }, [rooms, seats, selectedId, leftSeatIds]);
  const glockeActor = useMemo(
    () => sitzGlockeActorFromSeat(seats.find((row) => row.id === seatId) || null),
    [seatId, seats],
  );

  const noteElementGlocke = (targetSeatId: string, roomId: string, kind: 'added' | 'removed') => {
    const target = String(targetSeatId || '').trim();
    const room = String(roomId || '').trim();
    if (!target || !room || !seatId) return;
    noteSitzGlockePortalAction({
      targetSeatId: target,
      actorSeatId: seatId,
      actorName: glockeActor?.name,
      actorRole: glockeActor?.role,
      source: 'element',
      refId: room,
      kind,
    });
  };

  useEffect(() => {
    if (!selectedId) return;
    if (!rooms.some((room) => room.id === selectedId)) setSelectedId('');
  }, [rooms, selectedId]);

  useEffect(() => {
    setRemoveNote('');
    setReloadNote('');
    setReloadIds([]);
    setLeftSeatIds([]);
    setRemovingSeatId('');
    setRemovingRoom(false);
  }, [selectedId]);

  useEffect(() => {
    const token = getPortalPbToken();
    if (!token || !kanzleiId.trim()) {
      setSeats([]);
      return;
    }
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    const qs = new URLSearchParams({
      filter: `kanzlei_id="${kanzleiId.replace(/"/g, '')}"`,
      perPage: '100',
      sort: 'email',
    });
    let cancelled = false;
    void fetch(`${pbBase}/api/collections/customers/records?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data: { items?: Array<Record<string, unknown>> }) => {
        if (cancelled) return;
        const rows = Array.isArray(data.items) ? data.items : [];
        const parsed: KanzleiChatSeat[] = [];
        for (let i = 0; i < rows.length; i++) {
          const seat = readKanzleiChatSeat(rows[i]);
          if (seat) parsed.push(seat);
        }
        setSeats(parsed);
      })
      .catch(() => {
        if (!cancelled) setSeats([]);
      });
    return () => {
      cancelled = true;
    };
  }, [kanzleiId]);

  useEffect(() => {
    const token = getPortalPbToken();
    if (!token) {
      setApiRooms([]);
      return;
    }
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    let cancelled = false;
    void fetch(`${pbBase}/api/arkorbit/element-rooms`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        return { ok: res.ok, data };
      })
      .then((listed) => {
        if (cancelled || !listed.ok) return;
        const catalog = readElementRoomsResponse(listed.data);
        createdRoomsRef.current = createdRoomsRef.current.filter((row) =>
          catalog.some((item) => item.id === row.id),
        );
        setApiRooms(catalog);
        const pending = pendingOpenIdRef.current;
        if (pending) {
          const room = catalog.find((item) => item.id === pending) || null;
          if (room && seatIdInRoomSeatIds(seatId, room.seat_ids)) {
            pendingOpenIdRef.current = '';
            setSelectedId(room.id);
            setCreateNote('');
          } else if (room) {
            pendingOpenIdRef.current = '';
            setCreateNote(KANZLEI_CHAT_NOT_A_MEMBER);
          }
        }
      })
      .catch(() => {
        /* GET fehlgeschlagen: bisherige Liste bleibt. Kein lokales Verstecken. */
      });
    return () => {
      cancelled = true;
    };
  }, [catalogTick, seatId]);

  const toggleRoomsPanel = () => {
    setRoomsOpen((open) => {
      const next = !open;
      writeKanzleiChatRoomsOpen(next);
      return next;
    });
  };

  const active = rooms.find((room) => room.id === selectedId) || null;
  const activeSeatIsMember = seatIdInRoomSeatIds(seatId, active?.seat_ids);
  const loginToken =
    loginAttempt &&
    loginAttempt.seatId === seatId &&
    loginAttempt.seatMxid === seatMxid &&
    loginAttempt.roomId === active?.id
      ? loginAttempt.token
      : '';
  const chatPane = resolveKanzleiChatPane({
    seatMxid,
    seatId,
    memberSeatIds: active?.seat_ids,
    roomId: active?.id || '',
    sessionMxid: '',
    hasLoginToken: Boolean(loginToken),
    loginToken,
    historyFailed,
  });
  const homeserverHost = portalMatrixHomeserverUrl().replace(/^https:\/\//, '');

  useEffect(() => {
    setLoginAttempt((attempt) => {
      if (
        !attempt ||
        (attempt.seatId === seatId &&
          attempt.seatMxid === seatMxid &&
          attempt.roomId === selectedId)
      ) {
        return attempt;
      }
      return null;
    });
  }, [seatId, seatMxid, selectedId]);

  useEffect(() => {
    if (!active?.id || !activeSeatIsMember || !seatId.trim() || !seatMxid.trim()) return;
    if (
      loginAttempt &&
      loginAttempt.seatId === seatId &&
      loginAttempt.seatMxid === seatMxid &&
      loginAttempt.roomId === active.id
    ) {
      return;
    }
    const token = takeSeatLoginTokenForIframe(seatId);
    if (!token) return;
    setLoginAttempt({
      seatId,
      seatMxid,
      roomId: active.id,
      token,
    });
  }, [active?.id, activeSeatIsMember, attachTick, loginAttempt, seatId, seatMxid]);

  useEffect(() => {
    setHistoryFailed(false);
  }, [selectedId]);

  useEffect(() => {
    const built = kanzleiChatSeatAttachRequest(seatId);
    if (!built.ok) return;
    if (!elementSeatAttachDue(seatMxid, Date.now())) return;
    const pbToken = getPortalPbToken();
    if (!pbToken) return;
    markElementSeatAttach(seatMxid, Date.now());
    let cancelled = false;
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    void fetch(`${pbBase}/api/arkorbit/element-seat-attach`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${pbToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(built.body),
    })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        return { status: res.status, data };
      })
      .then(({ status, data }) => {
        if (cancelled) return;
        const attached = elementSeatAttachResult(data, status);
        if (!attached.ok || !elementSeatAttachMatchesSeat(attached.matrixUserId, seatMxid)) return;
        rememberSeatLoginToken(seatId, attached.loginToken);
        setAttachTick((tick) => tick + 1);
      })
      .catch(() => {
        /* Kein Token: Fläche bleibt ohne den Konto-Satz, solange die Sitz-MXID da ist. */
      });
    return () => {
      cancelled = true;
    };
  }, [seatId, seatMxid]);

  const submitCreate = (event: React.FormEvent) => {
    event.preventDefault();
    if (creating) return;
    const built = kanzleiChatCreateRequest({
      name: roomName,
      seatIds: inviteIds,
      inviteSeats,
    });
    if (!built.ok) {
      setCreateNote(built.message);
      return;
    }
    const token = getPortalPbToken();
    if (!token) {
      setCreateNote('Anmeldung erforderlich.');
      return;
    }
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    setCreating(true);
    setCreateNote('');
    void fetch(`${pbBase}/api/arkorbit/element-rooms`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(built.body),
    })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        return { res, data };
      })
      .then(({ res, data }) => {
        const result = kanzleiChatCreateResult(data, built.body.name);
        if (!res.ok || !result.ok) {
          setCreateNote(result.ok ? 'Raum nicht angelegt.' : result.message);
          return;
        }
        const room = result.room;
        createdRoomsRef.current = createdRoomsRef.current
          .filter((row) => row.id !== room.id)
          .concat([room]);
        setApiRooms((prev) => prev.filter((row) => row.id !== room.id).concat([room]));
        for (const invited of built.body.seat_ids) {
          noteElementGlocke(invited, room.id, 'added');
        }
        pendingOpenIdRef.current = room.id;
        setRoomName('');
        setInviteIds([]);
        setCreateNote('');
        setCatalogTick((tick) => tick + 1);
      })
      .catch(() => {
        setCreateNote('Raum nicht angelegt.');
      })
      .finally(() => {
        setCreating(false);
      });
  };

  const seatsAlreadyInRoom = useMemo(() => {
    const room = rooms.find((row) => row.id === selectedId) || null;
    return Array.isArray(room?.seat_ids) ? room.seat_ids : [];
  }, [rooms, selectedId]);
  const reloadSeats = useMemo(
    () => inviteSeats.filter((seat) => !seatsAlreadyInRoom.includes(seat.id)),
    [inviteSeats, seatsAlreadyInRoom],
  );

  const submitReload = (event: React.FormEvent) => {
    event.preventDefault();
    if (reloading || !selectedId) return;
    const built = kanzleiChatInviteExistingRequest({
      roomId: selectedId,
      seatIds: reloadIds,
      inviteSeats,
      alreadySeatIds: seatsAlreadyInRoom,
    });
    if (!built.ok) {
      setReloadNote(built.message);
      return;
    }
    const token = getPortalPbToken();
    if (!token) {
      setReloadNote('Anmeldung erforderlich.');
      return;
    }
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    setReloading(true);
    setReloadNote('');
    void fetch(`${pbBase}/api/arkorbit/element-rooms`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(built.body),
    })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        return { res, data };
      })
      .then(({ res, data }) => {
        const result = kanzleiChatInviteExistingResult(data, built.body.seat_ids, res.status);
        if (!result.ok) {
          setReloadNote(result.message);
          return;
        }
        for (const invited of built.body.seat_ids) {
          noteElementGlocke(invited, built.body.room_id, 'added');
        }
        setReloadIds([]);
        setReloadNote('');
        setCatalogTick((tick) => tick + 1);
      })
      .catch(() => {
        setReloadNote('Einladen nicht erreicht.');
      })
      .finally(() => {
        setReloading(false);
      });
  };

  const submitRemove = (seatId: string) => {
    if (removingSeatId) return;
    const built = kanzleiChatRemoveRequest({ roomId: selectedId, seatId });
    if (!built.ok) {
      setRemoveNote(built.message);
      return;
    }
    const token = getPortalPbToken();
    if (!token) {
      setRemoveNote('Anmeldung erforderlich.');
      return;
    }
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    const canSendBody = typeof Request !== 'undefined';
    const url = canSendBody
      ? `${pbBase}/api/arkorbit/element-rooms`
      : `${pbBase}/api/arkorbit/element-rooms?${built.query}`;
    setRemovingSeatId(seatId);
    setRemoveNote('');
    void fetch(url, {
      method: 'DELETE',
      headers: canSendBody
        ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        : { Authorization: `Bearer ${token}` },
      body: canSendBody ? JSON.stringify(built.body) : undefined,
    })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        return { res, data };
      })
      .then(({ res, data }) => {
        const result = kanzleiChatRemoveResult(
          data,
          { roomId: built.body.room_id, seatId: built.body.seat_id },
          res.status,
        );
        if (!result.ok) {
          setRemoveNote(result.message);
          return;
        }
        noteElementGlocke(result.seatId, built.body.room_id, 'removed');
        setLeftSeatIds((prev) => (prev.includes(result.seatId) ? prev : prev.concat(result.seatId)));
        setCatalogTick((tick) => tick + 1);
        setRemoveNote('');
      })
      .catch(() => {
        setRemoveNote('Entfernen nicht erreicht.');
      })
      .finally(() => {
        setRemovingSeatId('');
      });
  };

  const submitRemoveRoom = () => {
    if (removingSeatId || removingRoom) return;
    const room = rooms.find((row) => row.id === selectedId) || null;
    const built = kanzleiChatRemoveOwnSeatRequest({
      roomId: selectedId,
      ownSeatId: seatId,
      seatIds: room?.seat_ids,
    });
    if (!built.ok) {
      setRemoveNote(built.message);
      return;
    }
    const token = getPortalPbToken();
    if (!token) {
      setRemoveNote('Anmeldung erforderlich.');
      return;
    }
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    const canSendBody = typeof Request !== 'undefined';
    const url = canSendBody
      ? `${pbBase}/api/arkorbit/element-rooms`
      : `${pbBase}/api/arkorbit/element-rooms?${built.query}`;
    setRemovingRoom(true);
    setRemoveNote('');
    void fetch(url, {
      method: 'DELETE',
      headers: canSendBody
        ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        : { Authorization: `Bearer ${token}` },
      body: canSendBody ? JSON.stringify(built.body) : undefined,
    })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        return { res, data };
      })
      .then(async ({ res, data }) => {
        if (built.mode === 'archive') {
          const archived = kanzleiChatArchiveEmptyResult(
            data,
            { roomId: built.body.room_id },
            res.status,
          );
          if (!archived.ok) {
            setRemoveNote(archived.message);
            return;
          }
          const listed = await fetch(`${pbBase}/api/arkorbit/element-rooms`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then(async (getRes) => {
              const body = (await getRes.json().catch(() => ({}))) as Record<string, unknown>;
              return { ok: getRes.ok, data: body };
            })
            .catch(() => ({ ok: false as const, data: undefined }));
          if (!listed.ok) {
            setRemoveNote('Entfernen nicht bestätigt.');
            return;
          }
          const catalog = readElementRoomsResponse(listed.data);
          if (!kanzleiChatEmptyRoomDropped(true, catalog, archived.roomId)) {
            setRemoveNote('Entfernen nicht bestätigt.');
            return;
          }
          createdRoomsRef.current = createdRoomsRef.current.filter((row) =>
            catalog.some((item) => item.id === row.id),
          );
          setApiRooms(catalog);
          setRemoveNote('');
          return;
        }
        const result = kanzleiChatRemoveResult(
          data,
          { roomId: built.body.room_id, seatId: built.body.seat_id },
          res.status,
        );
        if (!result.ok) {
          setRemoveNote(result.message);
          return;
        }
        noteElementGlocke(result.seatId, built.body.room_id, 'removed');
        setLeftSeatIds((prev) => (prev.includes(result.seatId) ? prev : prev.concat(result.seatId)));
        setCatalogTick((tick) => tick + 1);
        setRemoveNote('');
      })
      .catch(() => {
        setRemoveNote('Entfernen nicht erreicht.');
      })
      .finally(() => {
        setRemovingRoom(false);
      });
  };

  const onRoomsResizeDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    roomsDragRef.current = { startX: event.clientX, startW: roomsWidthRef.current };
  };

  const onRoomsResizeMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = roomsDragRef.current;
    if (!drag) return;
    const next = clampKanzleiChatRoomsWidth(drag.startW + (event.clientX - drag.startX));
    roomsWidthRef.current = next;
    setRoomsWidth(next);
  };

  const onRoomsResizeUp = () => {
    if (!roomsDragRef.current) return;
    roomsDragRef.current = null;
    writeKanzleiChatRoomsWidth(roomsWidthRef.current);
  };

  return (
    <div
      className={`flex h-full min-h-0 w-full max-w-none flex-1 flex-col gap-3 overflow-hidden px-3 py-3 animate-fade-in ${KANZLEI_CHAT_GROUND} ${kanzleiChatLight}`}
      data-testid="portal-kanzlei-chat"
      data-layout="pane-fit"
    >
      <header className={`flex shrink-0 flex-wrap items-end justify-between gap-2 px-3.5 py-3 ${KANZLEI_CHAT_EDGE}`}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              aria-expanded={roomsOpen}
              aria-controls="portal-kanzlei-chat-rooms-panel"
              aria-label={roomsOpen ? 'Räume einklappen' : 'Räume aufklappen'}
              title={roomsOpen ? 'Räume einklappen' : 'Räume aufklappen'}
              data-testid="portal-kanzlei-chat-rooms-toggle"
              onClick={toggleRoomsPanel}
              className="relative h-7 w-7 shrink-0 p-0"
            >
              <KanzleiChatCube
                open={!roomsOpen}
                front={<ChevronLeft className="h-3.5 w-3.5" aria-hidden />}
                back={<ChevronRight className="h-3.5 w-3.5" aria-hidden />}
              />
            </button>
            <h1 className="text-sm font-semibold text-white tracking-tight">Kanzlei-Chat</h1>
          </div>
          <p className="m-0 mt-0.5 text-[11px] text-slate-500">
            Verschlüsselter Kanzlei-Raum — im Portal, auf eigenem Server in der EU.
          </p>
        </div>
        {homeserverHost ? (
          <p className="flex items-center gap-2 text-xs text-slate-500 m-0">
            <ShieldCheck className="w-4 h-4 text-emerald-500/80 flex-shrink-0" aria-hidden />
            Ende-zu-Ende verschlüsselt · EU
          </p>
        ) : null}
      </header>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 lg:flex-row">
        {roomsOpen ? (
        <aside
          id="portal-kanzlei-chat-rooms-panel"
          className={`flex max-h-[45%] min-h-0 w-full shrink-0 flex-col overflow-hidden p-3 lg:h-full lg:max-h-none lg:w-[var(--kanzlei-rooms-w)] lg:min-w-[240px] lg:max-w-[25vw] ${KANZLEI_CHAT_EDGE}`}
          style={{ ['--kanzlei-rooms-w' as string]: `${roomsWidth}px` }}
          data-testid="portal-kanzlei-chat-rooms"
        >
          <p className="m-0 shrink-0 text-[11px] uppercase tracking-wide text-slate-500">Räume</p>
          <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto custom-scrollbar" data-testid="portal-kanzlei-chat-room-list">
          {rooms.length === 0 ? (
            <p className="m-0 text-sm text-slate-400 leading-relaxed" data-testid="portal-kanzlei-chat-rooms-empty">
              Für diese Kanzlei ist noch kein Chat-Raum hinterlegt.
            </p>
          ) : (
            <ul className="m-0 list-none space-y-1 p-0">
              {rooms.map((room) => {
                const activeRoom = room.id === selectedId;
                const label = kanzleiChatRoomLabel(room);
                return (
                  <li key={room.id}>
                    <button
                      type="button"
                      aria-label={label}
                      data-testid={activeRoom ? 'portal-matrix-element-open' : 'portal-kanzlei-chat-room'}
                      onClick={() => {
                        setSelectedId(room.id);
                      }}
                      aria-pressed={activeRoom}
                      className={`portal-hell-hover w-full text-left rounded-lg border px-2.5 py-2 transition-colors ${
                        activeRoom
                          ? 'portal-hell-hover-on border-violet-400/70 bg-violet-600/15 text-white'
                          : 'border-transparent text-slate-300 hover:border-violet-400/30 hover:bg-violet-600/10'
                      }`}
                    >
                      <span className="block text-sm font-medium truncate">{label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {canManageRooms ? (
            <form
              onSubmit={submitCreate}
              className="flex flex-col gap-3 border-t border-violet-400/20 pt-3"
              data-testid="portal-kanzlei-chat-create"
            >
              <p className="m-0 text-[11px] uppercase tracking-wide text-slate-500">Raum anlegen</p>
              <label className="block space-y-1">
                <span className="text-xs text-slate-400">Name</span>
                <input
                  type="text"
                  value={roomName}
                  onChange={(ev) => setRoomName(ev.target.value)}
                  className={`${KANZLEI_CHAT_FIELD} px-2.5 py-2`}
                  placeholder="Zum Beispiel Team"
                  autoComplete="off"
                />
              </label>
              <fieldset className="m-0 border-0 p-0" data-testid="portal-kanzlei-chat-invite-seats">
                <legend className="mb-1 text-xs text-slate-400">Einladen</legend>
                {inviteSeats.length === 0 ? (
                  <p className="m-0 text-[11px] text-slate-500">Keine Sitze mit Element auf diesem Server.</p>
                ) : (
                  <ul className="m-0 list-none divide-y divide-violet-400/20 p-0">
                    {inviteSeats.map((seat) => {
                      const checked = inviteIds.includes(seat.id);
                      const lines = kanzleiChatSeatInviteLines(seat);
                      const label = kanzleiChatSeatLabel(seat);
                      return (
                        <li key={seat.id}>
                          <label
                            className="flex cursor-pointer items-start gap-2.5 py-2.5"
                            data-testid="portal-kanzlei-chat-invite-seat"
                          >
                            <input
                              type="checkbox"
                              aria-label={label}
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-violet-400/40 bg-[#07070a]"
                              checked={checked}
                              onChange={() => {
                                setInviteIds((prev) =>
                                  checked ? prev.filter((id) => id !== seat.id) : [...prev, seat.id],
                                );
                              }}
                            />
                            <span className="block min-w-0">
                              {lines.name ? (
                                <span className="block truncate text-sm font-medium leading-5 text-slate-100">
                                  {lines.name}
                                </span>
                              ) : null}
                              {lines.email ? (
                                <span
                                  className={
                                    lines.name
                                      ? 'mt-0.5 block truncate text-xs leading-4 text-slate-500'
                                      : 'block truncate text-sm leading-5 text-slate-200'
                                  }
                                  data-testid="portal-kanzlei-chat-invite-seat-email"
                                >
                                  {lines.email}
                                </span>
                              ) : null}
                              {!lines.name && !lines.email ? (
                                <span className="block truncate text-sm leading-5 text-slate-400">
                                  Zugang ohne Anzeigename
                                </span>
                              ) : null}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </fieldset>
              <button
                type="submit"
                disabled={creating}
                className={`${KANZLEI_CHAT_BTN} w-full justify-center`}
              >
                <UserPlus className="w-4 h-4" aria-hidden />
                Raum anlegen
              </button>
              {createNote ? (
                <p className="m-0 text-[11px] text-amber-200/90 leading-relaxed" data-testid="portal-kanzlei-chat-create-blocked">
                  {createNote}
                </p>
              ) : null}
            </form>
          ) : null}
          </div>
          {canManageRooms && active ? (
            <div
              className="mt-3 flex shrink-0 flex-col gap-2 border-t border-violet-400/20 pt-3"
              data-testid="portal-kanzlei-chat-selected-room"
            >
              <button
                type="button"
                data-testid="portal-kanzlei-chat-remove-room"
                disabled={removingRoom || Boolean(removingSeatId)}
                onClick={submitRemoveRoom}
                className={KANZLEI_CHAT_ICON}
                aria-label="Raum entfernen"
                title="Raum entfernen"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
              {removeNote ? (
                <p className="m-0 text-[11px] text-amber-200/90 leading-relaxed" data-testid="portal-kanzlei-chat-remove-blocked">
                  {removeNote}
                </p>
              ) : null}
              <form
                onSubmit={submitReload}
                className="flex flex-col gap-2"
                data-testid="portal-kanzlei-chat-reload-seats"
              >
                <p className="m-0 text-[11px] uppercase tracking-wide text-slate-500">Einladen</p>
                {reloadSeats.length === 0 ? (
                  <p className="m-0 text-[11px] text-slate-500">Keine weiteren Sitze mit Element.</p>
                ) : (
                  <ul className="m-0 max-h-36 list-none divide-y divide-violet-400/20 overflow-y-auto p-0">
                    {reloadSeats.map((seat) => {
                      const checked = reloadIds.includes(seat.id);
                      const lines = kanzleiChatSeatInviteLines(seat);
                      const label = kanzleiChatSeatLabel(seat);
                      return (
                        <li key={seat.id}>
                          <label className="flex cursor-pointer items-start gap-2 py-2">
                            <input
                              type="checkbox"
                              aria-label={`Einladen: ${label}`}
                              data-testid="portal-kanzlei-chat-reload-seat"
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-violet-400/40 bg-[#07070a]"
                              checked={checked}
                              onChange={() => {
                                setReloadIds((prev) =>
                                  checked ? prev.filter((id) => id !== seat.id) : [...prev, seat.id],
                                );
                              }}
                            />
                            <span className="block min-w-0">
                              {lines.name ? (
                                <span className="block truncate text-sm font-medium leading-5 text-slate-100">
                                  {lines.name}
                                </span>
                              ) : null}
                              {lines.email ? (
                                <span className="block truncate text-xs leading-4 text-slate-500">{lines.email}</span>
                              ) : null}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <button
                  type="submit"
                  disabled={reloading || reloadSeats.length === 0}
                  className={KANZLEI_CHAT_ICON}
                  aria-label="In den Raum laden"
                  title="In den Raum laden"
                >
                  <UserPlus className="h-3.5 w-3.5" aria-hidden />
                </button>
                {reloadNote ? (
                  <p className="m-0 text-[11px] text-amber-200/90 leading-relaxed" data-testid="portal-kanzlei-chat-reload-blocked">
                    {reloadNote}
                  </p>
                ) : null}
              </form>
              <div className="flex max-h-40 flex-col gap-2 overflow-y-auto custom-scrollbar" data-testid="portal-kanzlei-chat-room-members">
                <p className="m-0 text-[11px] uppercase tracking-wide text-slate-500">Im Raum</p>
                {seatsInRoom.length === 0 ? (
                  <p className="m-0 text-[11px] text-slate-500 leading-relaxed" data-testid="portal-kanzlei-chat-room-members-empty">
                    {KANZLEI_CHAT_ROOM_NO_STORED_SEATS}
                  </p>
                ) : (
                  <ul className="m-0 list-none divide-y divide-violet-400/20 p-0">
                    {seatsInRoom.map((seat) => {
                      const lines = kanzleiChatSeatInviteLines(seat);
                      const label = kanzleiChatSeatLabel(seat);
                      return (
                        <li key={seat.id} className="flex items-start justify-between gap-2 py-2.5" data-testid="portal-kanzlei-chat-room-member">
                          <span className="block min-w-0">
                            {lines.name ? (
                              <span className="block truncate text-sm font-medium leading-5 text-slate-100">
                                {lines.name}
                              </span>
                            ) : null}
                            {lines.email ? (
                              <span
                                className={
                                  lines.name
                                    ? 'mt-0.5 block truncate text-xs leading-4 text-slate-500'
                                    : 'block truncate text-sm leading-5 text-slate-200'
                                }
                              >
                                {lines.email}
                              </span>
                            ) : null}
                            {!lines.name && !lines.email ? (
                              <span className="block truncate text-sm leading-5 text-slate-400">
                                Zugang ohne Anzeigename
                              </span>
                            ) : null}
                          </span>
                          <button
                            type="button"
                            aria-label={`Entfernen: ${label}`}
                            data-testid="portal-kanzlei-chat-remove-seat"
                            disabled={removingSeatId === seat.id || removingRoom}
                            onClick={() => {
                              submitRemove(seat.id);
                            }}
                            className={KANZLEI_CHAT_BTN}
                          >
                            <UserMinus className="h-3.5 w-3.5" aria-hidden />
                            Entfernen
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
        </aside>
        ) : null}
        {roomsOpen ? (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Breite der Raumspalte"
            aria-valuemin={KANZLEI_CHAT_ROOMS_WIDTH_MIN}
            aria-valuemax={kanzleiChatRoomsWidthMax()}
            aria-valuenow={roomsWidth}
            data-testid="portal-kanzlei-chat-rooms-resize"
            onPointerDown={onRoomsResizeDown}
            onPointerMove={onRoomsResizeMove}
            onPointerUp={onRoomsResizeUp}
            onPointerCancel={onRoomsResizeUp}
            className="hidden w-1.5 shrink-0 cursor-col-resize touch-none rounded-full bg-transparent hover:bg-violet-400/40 lg:block"
          />
        ) : null}

        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" data-testid="portal-kanzlei-chat-thread">
          {chatPane.kind === 'no-account' ? (
            <section className={`${KANZLEI_CHAT_EDGE} min-h-0 flex-1 p-5`} data-testid="portal-kanzlei-chat-no-account">
              <p className="m-0 text-sm text-slate-400 leading-relaxed">{KANZLEI_CHAT_NO_ELEMENT_ACCOUNT}</p>
            </section>
          ) : chatPane.kind === 'boot' ? (
            <div ref={embedRef} className="flex min-h-0 flex-1 flex-col" data-testid="portal-matrix-kanzlei-raum">
              <ElementRoomEmbed
                src={chatPane.src}
                testIdPrefix="portal-matrix-element"
                grow
                showFallback={false}
                frameClassName="border border-violet-400/30 bg-[#07070a]"
                onError={() => setHistoryFailed(true)}
              />
            </div>
          ) : chatPane.kind === 'unloaded' ? (
            <section className={`${KANZLEI_CHAT_EDGE} min-h-0 flex-1 p-5`} data-testid="portal-kanzlei-chat-history-unloaded">
              <p className="m-0 text-sm text-slate-400 leading-relaxed">{KANZLEI_CHAT_HISTORY_UNLOADED}</p>
            </section>
          ) : chatPane.kind === 'not-member' ? (
            <section className={`${KANZLEI_CHAT_EDGE} min-h-0 flex-1 p-5`} data-testid="portal-kanzlei-chat-not-member">
              <p className="m-0 text-sm text-slate-400 leading-relaxed">{KANZLEI_CHAT_NOT_A_MEMBER}</p>
            </section>
          ) : chatPane.kind === 'seat-mxid' ? (
            <section className={`${KANZLEI_CHAT_EDGE} min-h-0 flex-1 p-5`} data-testid="portal-kanzlei-chat-seat-mxid">
              <p className="m-0 text-sm text-slate-400 leading-relaxed">{KANZLEI_CHAT_SEAT_MXID_ONLY}</p>
            </section>
          ) : rooms.length > 0 ? (
            <section className={`${KANZLEI_CHAT_EDGE} min-h-0 flex-1 p-5`} data-testid="portal-kanzlei-chat-pick-room">
              <p className="m-0 text-sm text-slate-400 leading-relaxed">
                Wähle einen Raum in der Liste. Der Verlauf erscheint hier.
              </p>
            </section>
          ) : (
            <section className={`${KANZLEI_CHAT_EDGE} min-h-0 flex-1 space-y-3 p-5`} data-testid="portal-matrix-honest-block">
              <p className="text-slate-400 text-sm leading-relaxed">
                Für Ihren Zugang ist noch kein Kanzlei-Raum hinterlegt. ARKORBIT richtet den verschlüsselten
                Raum beim Onboarding ein — bis dahin Aufnahme oder Datei unter Diktat.
              </p>
              <button
                type="button"
                onClick={onOpenDiktatAnleitung}
                className={KANZLEI_CHAT_ICON}
                aria-label="Zur Element-Anleitung"
                title="Zur Element-Anleitung"
              >
                <Smartphone className="w-3.5 h-3.5" aria-hidden />
              </button>
            </section>
          )}
        </section>
      </div>

      <p className="m-0 flex shrink-0 items-start gap-2 text-sm text-slate-500">
        <Smartphone className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
        Element App —{' '}
        <a
          href={ELEMENT_DOWNLOAD_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-violet-300 hover:text-violet-100"
        >
          <Link2 className="w-3.5 h-3.5" aria-hidden />
          element.io/download
        </a>
        <MessageCircle className="w-4 h-4 flex-shrink-0 mt-0.5 ml-2" aria-hidden />
        Sprachnachricht im Raum, Protokoll danach unter Dokumente.
      </p>
    </div>
  );
};
